import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { WindowService } from 'src/shared/providers/window.service';

import { ValueAccessorBase } from '../default-class/value-accessor';

import { SelectModel } from './select.model';

let nextTidySelectId = 0;

@Component({
    selector: 'tidy-select',
    encapsulation: ViewEncapsulation.None,
    templateUrl: 'select.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: SelectComponent,
            multi: true,
        },
    ],
    styleUrls: ['./select.component.scss'],
    standalone: false
})
export class SelectComponent extends ValueAccessorBase<any> implements OnInit, OnChanges, OnDestroy {

  @Input() disableElement: boolean;
  @Input() items: SelectModel[] = [];
  @Input() listedItems: SelectModel[] = [];
  @Input() label: string;
  // Accessible name for call sites that render their label as adjacent text
  // (a nearby header/body) instead of the component's own visible label, so the
  // control still has a non-empty name for screen readers without duplicating
  // that text on screen.
  @Input() ariaLabel: string;
  @Input() helperText: string;
  @Input() icon: string;
  @Input() form: FormControl = new FormControl();
  @Input() submitted: boolean;
  @Input() smallText: boolean;
  @Input() hideSearch: boolean;
  @Input() searchPlaceholder = '';
  @Input() public errorMessage: string;
  @Input() multiple = false;
  @Output() optionChange: EventEmitter<string> = new EventEmitter<string>();

  @Input() selectedItem: any;
  @ViewChild('selectContainer') selectContainer: ElementRef;
  @ViewChild('selectInput') selectInput: ElementRef;
  @ViewChild('dropdownSearchInput') dropdownSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('multiSelectSearchInput') multiSelectSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') auto: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  filteredItems: SelectModel[] = [];
  // Stable per-instance id so the visible label can be wired to the control via
  // aria-labelledby, giving screen readers the field's accessible name.
  readonly resolvedInputId = `tidy-select-${nextTidySelectId++}`;

  get labelId(): string {
    return `${this.resolvedInputId}-label`;
  }
  focused = false;
  panelWidth: number;
  showSearch: boolean;
  touchUpdate = false;
  searchControl: FormControl = new FormControl('');
  searchValue = '';
  isSearchFocused = false;
  private clickListener: () => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {
    super();
    this.clickListener = this.onDocumentClick.bind(this);
  }

  ngOnInit() {
    document.addEventListener('click', this.clickListener, true);
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
    this.listedItems = this.items || this.listedItems;
    this.filteredItems = [...this.listedItems];
    if (this.hideSearch) {
      this.showSearch = false;
    } else {
      this.showSearch = this.listedItems?.length > 5;
    }
    if (!this.selectedItem && this.value && this.listedItems?.length > 0) {
      this.selectedItem = this.listedItems.find((item) => this.value === item.value);
    }
    if (this.disableElement && this.form) {
      this.form.disable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.items) {
      this.listedItems = this.items || this.listedItems;
      this.filteredItems = [...this.listedItems];
      if (this.hideSearch) {
        this.showSearch = false;
      } else {
        this.showSearch = this.listedItems?.length > 5;
      }
      if (!changes.selectedItem || !changes.selectedItem.currentValue) {
        const currentValue = this.value || this.form?.value;
        this.selectedItem = this.listedItems.find((item) => currentValue === item.value);
      }
    }
    if (changes.selectedItem) {
      this.selectedItem = changes.selectedItem.currentValue;
    }
    if (changes.form) {
      const formValue = changes.form.currentValue.value;
      this.form.patchValue(formValue);
      if (formValue && this.listedItems?.length > 0) {
        this.selectedItem = this.listedItems.find((item) => item.value === formValue);
      }
    }
    if (changes.disableElement && this.form) {
      if (changes.disableElement.currentValue) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  onChange(event: any) {
    this.touchUpdate = true;
    if (this.multiple) {
      this.value = event.value;
      this.form.patchValue(event.value);
      this.optionChange.emit(event.value);
    } else {
      this.optionChange.emit(event.value);
      this.listedItems = this.items || this.listedItems;
      this.form.patchValue(event.value);
      this.selectedItem = this.listedItems.find((item) => event.value === item.value);
    }
  }

  searchItems() {
    try {
      const value = this.searchControl.value;
      this.listedItems = this.items.filter((item) => item.viewValue && item.viewValue.toLowerCase().includes(value.toLowerCase()));
    } catch (_error) { /* ignore filter errors */ }
  }

  setFocused(isOpen: boolean) {
    this.focused = isOpen;
    if (isOpen) {
      this.updatePanelWidth();
    }
  }

  updatePanelWidth() {
    if (this.selectContainer?.nativeElement) {
      this.panelWidth = this.selectContainer.nativeElement.offsetWidth;
    }
  }

  onContainerMouseDown(event: MouseEvent) {
    if (this.disableElement) {
      return;
    }
    event.preventDefault();
    if (this.selectInput?.nativeElement) {
      this.selectInput.nativeElement.focus();
    }
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  openPanel() {
    if (this.disableElement) {
      return;
    }
    if (this.selectInput?.nativeElement) {
      this.selectInput.nativeElement.focus();
    }
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  getDisplayValue(): string {
    if (this.selectedItem) {
      return this.selectedItem.viewValue || '';
    }
    if (this.value && this.listedItems?.length > 0) {
      const item = this.listedItems.find((i) => i.value === this.value);
      return item?.viewValue || '';
    }
    return '';
  }

  onInputFocus() {
    this.focused = true;
    this.updatePanelWidth();
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (this.showSearch) {
      setTimeout(() => {
        this.dropdownSearchInput?.nativeElement?.focus();
      }, 100);
    }
  }

  onInputBlur() {
    setTimeout(() => {
      if (this.isSearchFocused) {
        return;
      }
      this.focused = false;
      this.filteredItems = [...this.listedItems];
      if (this.selectInput?.nativeElement) {
        this.selectInput.nativeElement.value = this.getDisplayValue();
      }
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }, 150);
  }

  onSearchFocus(): void {
    this.isSearchFocused = true;
  }

  onSearchBlur(): void {
    this.isSearchFocused = false;
  }

  onSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchValue = input.value.toLowerCase();
    if (!searchValue) {
      this.filteredItems = [...this.listedItems];
    } else {
      this.filteredItems = this.listedItems.filter((item) =>
        item.viewValue && item.viewValue.toLowerCase().includes(searchValue)
      );
    }
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const selectedValue = event.option.value;
    this.touchUpdate = true;
    this.value = selectedValue;
    this.form.patchValue(selectedValue);
    this.selectedItem = this.listedItems.find((item) => item.value === selectedValue);
    this.optionChange.emit(selectedValue);
    this.filteredItems = [...this.listedItems];
  }

  isSelected(value: any): boolean {
    return this.value === value || this.form?.value === value || this.selectedItem?.value === value;
  }

  override writeValue(value: any) {
    super.writeValue(value);
    if (value && this.listedItems?.length > 0) {
      this.selectedItem = this.listedItems.find((item) => item.value === value);
    }
  }

  onDropdownSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchLower = input.value.toLowerCase();
    let newFilteredItems = !searchLower
      ? [...this.listedItems]
      : this.listedItems.filter((item) =>
          item.viewValue?.toLowerCase().includes(searchLower)
        );
    if (newFilteredItems.length === 0) {
      newFilteredItems = [{ value: '__no_results__', viewValue: 'No results found' }];
    }
    this.filteredItems = newFilteredItems;
    this.searchValue = input.value;
    setTimeout(() => {
      if (this.autocompleteTrigger && !this.autocompleteTrigger.panelOpen) {
        this.autocompleteTrigger.openPanel();
      }
      input.focus();
    }, 10);
  }

  clearDropdownSearch(inputElement?: HTMLInputElement): void {
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (inputElement) {
      inputElement.value = '';
    }
  }

  onMultiSelectOpened(isOpen: boolean): void {
    if (isOpen) {
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
      if (this.showSearch) {
        setTimeout(() => {
          this.multiSelectSearchInput?.nativeElement?.focus();
        }, 100);
      }
    } else {
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
    }
  }

  onMultiSelectSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchLower = input.value.toLowerCase();
    this.searchValue = input.value;
    if (!searchLower) {
      this.filteredItems = [...this.listedItems];
    } else {
      const filtered = this.listedItems.filter((item) =>
        item.viewValue?.toLowerCase().includes(searchLower)
      );
      this.filteredItems = filtered.length > 0
        ? filtered
        : [{ value: '__no_results__', viewValue: 'No results found' }];
    }
  }

  clearMultiSelectSearch(): void {
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (this.multiSelectSearchInput?.nativeElement) {
      this.multiSelectSearchInput.nativeElement.value = '';
      this.multiSelectSearchInput.nativeElement.focus();
    }
  }

  getMultiSelectDisplayValue(): string {
    const selectedValues = this.form?.value;
    if (!selectedValues || !Array.isArray(selectedValues) || selectedValues.length === 0) {
      return this.helperText || '';
    }
    const selectedItems = this.listedItems.filter((item) =>
      selectedValues.includes(item.value)
    );
    if (selectedItems.length === 0) {
      return this.helperText || '';
    }
    return selectedItems.map((item) => item.viewValue).join(', ');
  }

  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!this.el.nativeElement.contains(target) && !target.closest('.cdk-overlay-container')) {
      this.focused = false;
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.clickListener, true);
  }
}